import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import { motion } from "framer-motion";
import { FiArrowRight, FiMapPin } from "react-icons/fi";
import { usePosts } from "../hooks/usePosts";
import type { BlogPost } from "../types";
import "leaflet/dist/leaflet.css";
import styles from "./MappedStories.module.css";

const ZOOM_THRESHOLD = 5;

function createImageIcon(imageUrl: string) {
  return L.divIcon({
    className: styles.markerIcon,
    html: `
      <div class="${styles.markerContainer}">
        <div class="${styles.markerPulse}"></div>
        <div class="${styles.markerImageWrap}">
          <img src="${imageUrl}" alt="" />
        </div>
        <div class="${styles.markerPin}"></div>
      </div>
    `,
    iconSize: [56, 72],
    iconAnchor: [28, 72],
    popupAnchor: [0, -76],
  });
}

const dotIcon = L.divIcon({
  className: styles.markerIcon,
  html: `<div class="${styles.dotMarker}"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  popupAnchor: [0, -8],
});

function ZoomAwareMarkers({ posts }: { posts: BlogPost[] }) {
  const [zoom, setZoom] = useState<number | null>(null);

  useMapEvents({
    zoom(e) {
      setZoom(e.target.getZoom());
    },
    zoomend(e) {
      setZoom(e.target.getZoom());
    },
    load(e) {
      setZoom(e.target.getZoom());
    },
  });

  const showImages = zoom !== null && zoom >= ZOOM_THRESHOLD;

  return (
    <>
      {posts.map((post) => (
        <Marker
          key={post.id}
          position={[post.coordinates!.lat, post.coordinates!.lng]}
          icon={showImages ? createImageIcon(post.coverImage) : dotIcon}
        >
          <Popup className={styles.popup}>
            <div className={styles.popupContent}>
              <img
                src={post.coverImage}
                alt={post.title}
                className={styles.popupImage}
              />
              <div className={styles.popupBody}>
                <span className={styles.popupLocation}>
                  <FiMapPin size={11} /> {post.location}, {post.country}
                </span>
                <h3 className={styles.popupTitle}>{post.title}</h3>
                <p className={styles.popupExcerpt}>{post.excerpt}</p>
                <Link to={`/blog/${post.id}`} className={styles.popupLink}>
                  Read Story <FiArrowRight size={12} />
                </Link>
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}

export default function MappedStories() {
  const { posts } = usePosts();
  const postsWithCoords = posts.filter((p) => p.coordinates);

  const bounds = useMemo(() => {
    if (postsWithCoords.length === 0) return undefined;
    const lats = postsWithCoords.map((p) => p.coordinates!.lat);
    const lngs = postsWithCoords.map((p) => p.coordinates!.lng);
    return L.latLngBounds(
      [Math.min(...lats) - 5, Math.min(...lngs) - 10],
      [Math.max(...lats) + 5, Math.max(...lngs) + 10]
    );
  }, [postsWithCoords]);

  return (
    <main className={styles.page}>
      <section className={styles.header}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className={styles.label}>Explore</span>
          <h1 className={styles.title}>Mapped Stories</h1>
          <p className={styles.subtitle}>
            Every pin is a memory. Click to explore.
          </p>
        </motion.div>
      </section>

      <motion.section
        className={styles.mapSection}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className={styles.mapContainer}>
          <MapContainer
            bounds={bounds}
            scrollWheelZoom={true}
            className={styles.map}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            <ZoomAwareMarkers posts={postsWithCoords} />
          </MapContainer>
        </div>
      </motion.section>

      <section className={styles.legend}>
        <div className={styles.legendInner}>
          <h2 className={styles.legendTitle}>All Destinations</h2>
          <div className={styles.legendGrid}>
            {postsWithCoords.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={`/blog/${post.id}`} className={styles.legendCard}>
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className={styles.legendImage}
                  />
                  <div className={styles.legendInfo}>
                    <span className={styles.legendLocation}>
                      <FiMapPin size={11} /> {post.location}
                    </span>
                    <span className={styles.legendCountry}>{post.country}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
